import React from 'react';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { useTheme } from '../../Theme/ThemeProvider';
import MessageContentRenderer from './MessageContentRenderer';

interface CollapsibleCodeBlockProps {
  codeContent: string;
  isStreaming: boolean;
}

const CollapsibleCodeBlock: React.FC<CollapsibleCodeBlockProps> = ({ codeContent, isStreaming }) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const hasAutoCollapsedRef = React.useRef(false);
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  // Auto-collapse when streaming ends
  React.useEffect(() => {
    if (!isStreaming && !hasAutoCollapsedRef.current && codeContent.trim().length > 0) {
      hasAutoCollapsedRef.current = true;
      setIsOpen(false);
    }
  }, [isStreaming, codeContent]);

  // Reset when new streaming starts
  React.useEffect(() => {
    if (isStreaming) {
      hasAutoCollapsedRef.current = false;
      setIsOpen(true);
    }
  }, [isStreaming]);

  if (!codeContent || !codeContent.trim()) return null;

  return (
    <div
      className="my-3"
      style={{
        border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid var(--color-border)',
        borderRadius: '8px',
        overflow: 'hidden',
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Hide code' : 'Show code'}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 600,
          fontSize: '0.8rem',
          color: 'var(--color-text)',
        }}
      >
        <ChevronDownIcon
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform .25s ease',
            width: 16,
            height: 16,
            flexShrink: 0,
          }}
        />
        <span>Code</span>
        <span style={{ marginLeft: 'auto', fontSize: '0.7rem', opacity: 0.6 }}>
          {isOpen ? 'click to collapse' : 'click to expand'}
        </span>
      </button>

      {/* Collapsible Body */}
      {isOpen && (
        <div style={{ padding: '0 12px 12px', maxHeight: '500px', overflowY: 'auto' }}>
          <MessageContentRenderer content={codeContent} disableNormalization />
        </div>
      )}
    </div>
  );
};

export default CollapsibleCodeBlock;
